import { useState, useCallback, useRef, useMemo } from "react";
import {
  Loader2,
  ChevronDown,
  ChevronUp,
  GitCompareArrows,
} from "lucide-react";
import { tileCardStyle, tileHoverHandlers } from "../utils";
import ReportCitationButton from "../../Reports/ReportCitationButton";
import ArticleDetailPanel from "../../../Universal/ArticleCardDetailPanel";
import CitationLoadingAnimation from "../../../Universal/CitationLoadingAnimation";
import { useRightSidebar } from "../../../contexts/RightSidebarContext";
import { getTheme } from "../../../Universal/useNavTheme";
import { getDocIdData } from "../../../../api/universal";
import { scrapePublicCitation } from "../../../../api/chat";

const formatTimestamp = (ts) => {
  if (!ts) return "";
  try {
    const num = typeof ts === "string" ? Number(ts) : ts;
    const date = new Date(
      typeof num === "number" && num < 1e12 ? num * 1000 : num,
    );
    if (isNaN(date.getTime())) return ts;
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return ts;
  }
};

const applyInlineStyles = (text) => {
  if (!text) return null;
  const parts = [];
  const regex = /(\*\*[^*]+\*\*|\*[^*]+\*)/g;
  let match,
    lastIndex = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    const raw = match[0];
    if (raw.startsWith("**"))
      parts.push(
        <strong
          key={match.index}
          className="font-semibold text-zinc-700 dark:text-zinc-200"
        >
          {raw.slice(2, -2)}
        </strong>,
      );
    else parts.push(<em key={match.index}>{raw.slice(1, -1)}</em>);
    lastIndex = match.index + raw.length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts.length === 1 && typeof parts[0] === "string" ? parts[0] : parts;
};

const inlineMarkdown = (
  text,
  citations,
  onSourceClick,
  hoveredRef,
  onHoverIn,
  onHoverOut,
  activeSourceId,
  citationInteractions,
) => {
  if (!citations || citations.length === 0) return applyInlineStyles(text);
  const parts = [];
  const regex = /\[(\d+)\]/g;
  let match,
    lastIndex = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex)
      parts.push(
        <span key={"t-" + lastIndex}>
          {applyInlineStyles(text.slice(lastIndex, match.index))}
        </span>,
      );
    const idx = parseInt(match[1], 10);
    const citation = citations.find((c) => c.index === idx);
    if (citation)
      parts.push(
        <ReportCitationButton
          key={"cite-" + match.index + "-" + idx}
          index={idx}
          citation={citation}
          hoveredRef={hoveredRef}
          onHoverIn={onHoverIn}
          onHoverOut={onHoverOut}
          onSourceClick={() => onSourceClick(citation)}
          activeSourceId={activeSourceId}
          citationInteractions={citationInteractions}
        />,
      );
    else parts.push(<span key={"miss-" + match.index}>{match[0]}</span>);
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length)
    parts.push(
      <span key="t-end">{applyInlineStyles(text.slice(lastIndex))}</span>,
    );
  return parts.length > 0 ? parts : applyInlineStyles(text);
};

const renderMarkdown = (
  text,
  citations,
  onSourceClick,
  hoveredRef,
  onHoverIn,
  onHoverOut,
  activeSourceId,
  citationInteractions,
) => {
  if (!text) return null;
  const lines = text.split("\n");
  const elements = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      elements.push(<div key={"gap-" + i} className="h-1.5" />);
      i++;
      continue;
    }
    if (/^-{3,}$/.test(line.trim())) {
      elements.push(
        <hr
          key={"hr-" + i}
          className="border-t border-zinc-100 dark:border-zinc-800 my-2"
        />,
      );
      i++;
      continue;
    }
    if (line.startsWith("## ")) {
      elements.push(
        <p
          key={"h2-" + i}
          className="text-[12px] font-semibold text-zinc-600 dark:text-zinc-300 mt-3 mb-0.5 uppercase tracking-wider"
        >
          {line.slice(3).replace(/\*\*/g, "")}
        </p>,
      );
      i++;
      continue;
    }
    if (line.startsWith("### ")) {
      elements.push(
        <p
          key={"h3-" + i}
          className="text-[11px] font-semibold text-zinc-500 dark:text-zinc-400 mt-2 mb-0.5 uppercase tracking-wider"
        >
          {line.slice(4).replace(/\*\*/g, "")}
        </p>,
      );
      i++;
      continue;
    }
    if (line.startsWith("# ")) {
      elements.push(
        <p
          key={"h1-" + i}
          className="text-[13px] font-semibold text-zinc-700 dark:text-zinc-200 mt-3 mb-1"
        >
          {line.slice(2).replace(/\*\*/g, "")}
        </p>,
      );
      i++;
      continue;
    }
    if (/^[-*] /.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*] /.test(lines[i])) {
        items.push(lines[i].slice(2));
        i++;
      }
      elements.push(
        <ul key={"ul-" + i} className="pl-2 flex flex-col gap-0.5 my-1">
          {items.map((item, idx) => (
            <li
              key={idx}
              className="flex gap-2 text-[13px] leading-relaxed text-zinc-600 dark:text-zinc-300"
            >
              <span className="mt-[7px] h-1 w-1 rounded-full bg-zinc-400 dark:bg-zinc-500 shrink-0" />
              <span>
                {inlineMarkdown(
                  item,
                  citations,
                  onSourceClick,
                  hoveredRef,
                  onHoverIn,
                  onHoverOut,
                  activeSourceId,
                  citationInteractions,
                )}
              </span>
            </li>
          ))}
        </ul>,
      );
      continue;
    }
    if (line.includes("|")) {
      elements.push(
        <p
          key={"tbl-" + i}
          className="text-[12px] font-mono text-zinc-500 dark:text-zinc-400 leading-snug whitespace-pre-wrap"
        >
          {line}
        </p>,
      );
      i++;
      continue;
    }
    elements.push(
      <p
        key={"p-" + i}
        className="text-[13px] leading-relaxed text-zinc-600 dark:text-zinc-300"
      >
        {inlineMarkdown(
          line,
          citations,
          onSourceClick,
          hoveredRef,
          onHoverIn,
          onHoverOut,
          activeSourceId,
          citationInteractions,
        )}
      </p>,
    );
    i++;
  }
  return elements;
};

const computeWordDiff = (oldText, newText) => {
  const ow = (oldText || "").split(/(\s+)/),
    nw = (newText || "").split(/(\s+)/);
  const m = ow.length,
    n = nw.length;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] =
        ow[i - 1] === nw[j - 1]
          ? dp[i - 1][j - 1] + 1
          : Math.max(dp[i - 1][j], dp[i][j - 1]);
  const result = [];
  let i = m,
    j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && ow[i - 1] === nw[j - 1]) {
      result.unshift({ type: "same", text: ow[i - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      result.unshift({ type: "added", text: nw[j - 1] });
      j--;
    } else {
      result.unshift({ type: "removed", text: ow[i - 1] });
      i--;
    }
  }
  return result;
};

const buildCitations = (sourcesList) => {
  if (!Array.isArray(sourcesList) || sourcesList.length === 0) return [];
  return sourcesList.map((s, i) =>
    s.type === "document_id"
      ? {
          index: i + 1,
          doc_id: s.value,
          url: "",
          title: s.title || "",
          like: !!s.like,
          dislike: !!s.dislike,
          catalog: !!s.catalog,
        }
      : { index: i + 1, url: s.value, doc_id: "", title: "" },
  );
};

const stripCitations = (text) => (!text ? "" : text.replace(/\[\d+\]/g, ""));

const DiffView = ({ diffParts, t }) => {
  const oldParts = [],
    newParts = [];
  diffParts.forEach((p, i) => {
    if (p.type === "same") {
      oldParts.push(<span key={i}>{p.text}</span>);
      newParts.push(<span key={i}>{p.text}</span>);
    } else if (p.type === "removed")
      oldParts.push(
        <span
          key={i}
          className="bg-red-100 text-red-800 line-through dark:bg-red-900/30 dark:text-red-300"
        >
          {p.text}
        </span>,
      );
    else
      newParts.push(
        <span
          key={i}
          className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300"
        >
          {p.text}
        </span>,
      );
  });
  return (
    <div className="grid grid-cols-2 gap-3">
      <div
        style={{
          borderRight: `1px solid ${t.divider}`,
          color: t.textSecondary,
        }}
        className="pr-3"
      >
        <span
          style={{ color: t.textMuted }}
          className="text-[10px] uppercase tracking-wider font-medium block mb-2"
        >
          Previous
        </span>
        <p
          className="text-sm leading-relaxed whitespace-pre-wrap"
          style={{ margin: 0 }}
        >
          {oldParts}
        </p>
      </div>
      <div style={{ color: t.textSecondary }} className="pl-1">
        <span
          style={{ color: t.textMuted }}
          className="text-[10px] uppercase tracking-wider font-medium block mb-2"
        >
          Current
        </span>
        <p
          className="text-sm leading-relaxed whitespace-pre-wrap"
          style={{ margin: 0 }}
        >
          {newParts}
        </p>
      </div>
    </div>
  );
};

const SourcesDropdown = ({ sourcesList, t, accent }) => {
  const [open, setOpen] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const copy = async (e, url, key) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(url);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1600);
    } catch {}
  };
  const urls = (sourcesList || [])
    .map((s) => s.value || s.url || s.doc_id)
    .filter(Boolean);
  if (urls.length === 0) return null;
  return (
    <div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        style={{ color: open ? accent : t.textMuted }}
        className="flex items-center gap-1 text-[11px] font-medium hover:opacity-75 transition-all"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-3 w-3 shrink-0"
        >
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
        </svg>
        {urls.length} source{urls.length !== 1 ? "s" : ""}
        <ChevronDown
          className="h-3 w-3 transition-transform duration-150"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
          strokeWidth={2}
        />
      </button>
      {open && (
        <div
          className="mt-2 flex flex-col gap-1"
          onClick={(e) => e.stopPropagation()}
        >
          {urls.map((url, i) => {
            const isCopied = copiedKey === i;
            const display = url
              .replace(/^https?:\/\/(www\.)?/, "")
              .replace(/\/$/, "");
            return (
              <div
                key={i}
                style={{
                  background: t.labelBg,
                  border: `1px solid ${t.divider}`,
                }}
                className="flex items-center gap-2 rounded-lg px-3 py-1.5"
              >
                <span
                  style={{ color: t.textMuted }}
                  className="flex-1 min-w-0 text-[11px] font-mono truncate"
                  title={url}
                >
                  {display}
                </span>
                <button
                  onClick={(e) => copy(e, url, i)}
                  style={{ color: isCopied ? accent : t.textMuted }}
                  className="shrink-0 transition-colors hover:opacity-75"
                  title="Copy URL"
                >
                  {isCopied ? (
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2.5}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-3 w-3"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : (
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-3 w-3"
                    >
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </svg>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const COLLAPSE_THRESHOLD = 480;

const TileReport = ({ data = {}, t, companyName = "" }) => {
  const { setRightComponent, setIsRightOpen, setRightSidebarMode } =
    useRightSidebar();
  const [citationInteractions, setCitationInteractions] = useState(() => {
    const initial = {};
    (data.sources_list || []).forEach((s) => {
      if (
        s.type === "document_id" &&
        s.value &&
        (s.like || s.dislike || s.catalog)
      )
        initial[s.value] = {
          like: !!s.like,
          dislike: !!s.dislike,
          catalog: !!s.catalog,
        };
    });
    return initial;
  });
  const [activeSourceId, setActiveSourceId] = useState(null);
  const hoveredRef = useRef(null);
  const versions = data.versions || [data];
  const [selectedVersionIdx, setSelectedVersionIdx] = useState(0);
  const [showChanges, setShowChanges] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const selectedVersion = versions[selectedVersionIdx] || versions[0];
  const previousVersion = versions[selectedVersionIdx + 1] || null;
  const diffParts = useMemo(() => {
    if (!showChanges || !previousVersion) return null;
    return computeWordDiff(
      stripCitations(previousVersion.content || ""),
      stripCitations(selectedVersion.content || ""),
    );
  }, [showChanges, selectedVersion, previousVersion]);
  const onHoverIn = useCallback((idx) => {
    hoveredRef.current = idx;
  }, []);
  const onHoverOut = useCallback(() => {
    hoveredRef.current = null;
  }, []);
  const openArticlePanel = useCallback(
    (citationKey, article) => {
      const dark = document.documentElement.classList.contains("dark");
      setRightComponent(
        <ArticleDetailPanel
          key={citationKey}
          article={article}
          cardId={citationKey}
          t={getTheme(dark)}
          onInteract={(_c, _d, state) =>
            setCitationInteractions((prev) => ({
              ...prev,
              [citationKey]: state,
            }))
          }
        />,
      );
    },
    [setRightComponent],
  );
  const handleSourceClick = useCallback(
    (citation) => {
      const key = citation.doc_id || citation.url;
      setActiveSourceId(key);
      setRightSidebarMode(null);
      setIsRightOpen(true);
      if (citation.doc_id) {
        setRightComponent(
          <div className="p-4 text-[13px] text-zinc-500 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>,
        );
        getDocIdData(citation.doc_id)
          .then((r) => {
            const a = r?.data;
            if (!a) return;
            openArticlePanel(key, {
              ...a,
              like: !!a.like,
              dislike: !!a.dislike,
              catalog: !!a.catalog,
            });
          })
          .catch(() =>
            setRightComponent(
              <div className="p-6 text-center text-[13px]">
                Unable to load source
              </div>,
            ),
          );
      } else if (citation.url) {
        setRightComponent(
          <div className="h-full">
            <CitationLoadingAnimation />
          </div>,
        );
        scrapePublicCitation({ url: citation.url })
          .then((res) => {
            const d = res?.data;
            if (!d?.success && !d?.already_saved) return;
            const article = {
              document_id: d.doc_id,
              title: d.title || d.domain || "Untitled",
              url: d.url || citation.url,
              source: d.source || d.domain || "",
              content: d.content || "",
              description: d.description || "",
              screenshot: d.screenshot || "",
              screenshot_full: d.screenshot_full || "",
              target_type: d.target_type || "web_citation",
              timestamp: d.date_published
                ? new Date(d.date_published).toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })
                : "",
              like: !!d.like,
              dislike: !!d.dislike,
              catalog: !!d.catalog,
            };
            if (d.like || d.dislike || d.catalog)
              setCitationInteractions((prev) => ({
                ...prev,
                [key]: {
                  like: !!d.like,
                  dislike: !!d.dislike,
                  catalog: !!d.catalog,
                },
              }));
            setRightComponent(
              <CitationLoadingAnimation
                isComplete
                statusMessage="Source captured"
                onCompleteFinished={() => openArticlePanel(key, article)}
              />,
            );
          })
          .catch(() =>
            setRightComponent(
              <div className="p-6 text-center text-[13px]">
                Unable to load citation
              </div>,
            ),
          );
      }
    },
    [setRightComponent, setIsRightOpen, setRightSidebarMode, openArticlePanel],
  );

  const targetType =
    typeof data.target_type === "string" ? data.target_type.trim() : "";
  const sectionTitle =
    typeof data.title === "string" && data.title.trim()
      ? data.title.trim()
      : targetType.replace(/_/g, " ");
  const content =
    typeof selectedVersion.content === "string"
      ? selectedVersion.content.trim()
      : "";
  if (!targetType && !content) return null;
  const citations = buildCitations(selectedVersion.sources_list);
  const timestamp = formatTimestamp(selectedVersion.timestamp);
  const accent = t.accent || "#0EA5E9";
  const isLong = content.length > COLLAPSE_THRESHOLD;
  const displayContent =
    isLong && !expanded && !showChanges
      ? content.slice(0, COLLAPSE_THRESHOLD).replace(/\s+\S*$/, "")
      : content;

  return (
    <div
      className="border border-surfaceBorder border-t-2 border-t-sky-400 overflow-hidden relative bg-reortTileBg scroll-mt-20"
      style={tileCardStyle(t)}
      {...tileHoverHandlers(t)}
    >
      <div className="px-5 pt-4 pb-3 relative">
        <div className="flex items-start justify-between gap-3 mb-2">
          <h3 className="text-[14px] font-semibold text-zinc-800 dark:text-zinc-100 leading-snug flex-1 min-w-0">
            {sectionTitle}
          </h3>
          <div className="flex items-center gap-2 shrink-0">
            {versions.length > 1 && (
              <div className="relative shrink-0">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setDropdownOpen((v) => !v);
                  }}
                  style={{
                    background: t.labelBg,
                    border: `1px solid ${t.labelBorder}`,
                    color: t.labelText,
                  }}
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium"
                >
                  {timestamp}
                  <ChevronDown className="h-3 w-3" />
                </button>
                {dropdownOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDropdownOpen(false);
                      }}
                    />
                    <div
                      style={{
                        background: t.pageBg,
                        border: `1px solid ${t.cardBorder}`,
                      }}
                      className="absolute right-0 top-full mt-1 z-20 rounded-md shadow-lg min-w-[140px] py-1"
                    >
                      {versions.map((v, idx) => (
                        <button
                          key={idx}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedVersionIdx(idx);
                            setDropdownOpen(false);
                          }}
                          style={{
                            color:
                              idx === selectedVersionIdx
                                ? t.badgeText
                                : t.textSecondary,
                            background:
                              idx === selectedVersionIdx
                                ? t.badgeBg
                                : "transparent",
                          }}
                          className="w-full text-left px-3 py-1.5 text-[11px] hover:opacity-80 transition-opacity"
                        >
                          {formatTimestamp(v.timestamp)}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {targetType && (
            <span
              style={{
                background: `${accent}18`,
                border: `1px solid ${accent}30`,
                color: accent,
              }}
              className="inline-block rounded-md px-2 py-0.5 text-[11px] font-medium"
            >
              {targetType}
            </span>
          )}
          {versions.length <= 1 && timestamp && (
            <span style={{ color: t.textMuted }} className="text-[11px]">
              {timestamp}
            </span>
          )}
          {!!previousVersion && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowChanges((v) => !v);
              }}
              style={{
                background: showChanges ? "rgba(14,165,233,0.1)" : t.labelBg,
                border: `1px solid ${showChanges ? "rgba(14,165,233,0.4)" : t.labelBorder}`,
                color: showChanges ? "#0ea5e9" : t.labelText,
              }}
              className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium transition-all"
            >
              <GitCompareArrows className="h-3 w-3" />
              Changes
            </button>
          )}
        </div>
      </div>
      <div style={{ borderTop: `1px solid ${t.divider}` }} className="mx-5" />
      <div className="px-5 pt-3 pb-1 relative">
        {content &&
          (showChanges && diffParts ? (
            <DiffView diffParts={diffParts} t={t} />
          ) : (
            <div>
              {renderMarkdown(
                displayContent,
                citations,
                handleSourceClick,
                hoveredRef,
                onHoverIn,
                onHoverOut,
                activeSourceId,
                citationInteractions,
              )}
              {isLong && !expanded && !showChanges && (
                <div className="relative mt-[-20px] h-8 pointer-events-none bg-gradient-to-b from-transparent to-reortTileBg" />
              )}
            </div>
          ))}
      </div>
      <div className="px-5 pb-4 pt-1 flex flex-col gap-2">
        {selectedVersion.sources_list?.length > 0 && (
          <SourcesDropdown
            sourcesList={selectedVersion.sources_list}
            t={t}
            accent={accent}
          />
        )}
        {isLong && !showChanges && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
            style={{ color: accent }}
            className="flex items-center gap-1 text-[12px] font-medium hover:opacity-75 transition-opacity self-start"
          >
            {expanded ? (
              <>
                <ChevronUp className="h-3.5 w-3.5" />
                Show less
              </>
            ) : (
              <>
                <ChevronDown className="h-3.5 w-3.5" />
                Show more
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default TileReport;
